import { PublicHeader } from './PublicHeader';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Calendar, MapPin, Camera, ChevronRight, Download, Filter, Image as ImageIcon, LoaderCircle, RefreshCw, Search, UserRound, X, ZoomIn } from 'lucide-react';
import { api, type EventInfo, type EventListItem, type Photo, type Runner } from './api';

const SelfieSearch = lazy(() => import('./SelfieSearch'));

function getRoute() {
  const match = window.location.pathname.match(/^\/eventos\/([^/]+)/);
  const params = new URLSearchParams(window.location.search);
  return {
    slug: match?.[1] || null,
    initialBib: params.get('bib') || '',
    preview: params.get('preview') || undefined
  };
}

function message(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

const eventDetails: Record<string, { date: string; location: string; cover: string }> = {
  'reto-tenan-2026': {
    date: '13 de septiembre de 2026', location: 'Santa María Palapa, Estado de México',
    cover: '/reto-tenan-2026.png'
  },
  'carrera-de-la-tuna-2026': {
    date: '2 de agosto de 2026', location: 'Teotihuacán, Estado de México',
    cover: 'https://runexperiences.com.mx/wp-content/plugins/runexperiences-results-analytics/assets/images/resultados-tuna-2026.webp'
  }
};

export default function App() {
  const [currentRoute, setCurrentRoute] = useState(getRoute());
  const { slug, initialBib, preview } = currentRoute;

  // Home / Events list state
  const [eventsList, setEventsList] = useState<EventListItem[]>([]);
  const [loadingEvents, setLoadingEvents] = useState<boolean>(!slug);

  // Single Event state
  const [event, setEvent] = useState<EventInfo | null>(null);
  const [query, setQuery] = useState(initialBib);
  const [selectedDistance, setSelectedDistance] = useState<string>('');
  const [results, setResults] = useState<Runner[]>([]);
  const [runner, setRunner] = useState<Runner | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [allEventPhotos, setAllEventPhotos] = useState<Photo[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState<'event' | 'search' | 'photos' | 'more' | null>(slug ? 'event' : null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Photo | null>(null);
  const [selfieOpen, setSelfieOpen] = useState(false);
  const [selfieSession, setSelfieSession] = useState(0);
  const [facePhotos, setFacePhotos] = useState<Photo[]>([]);
  const [selfieSearched, setSelfieSearched] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const initialSearchStarted = useRef(false);

  // Sync route on popstate
  useEffect(() => {
    const handlePopState = () => setCurrentRoute(getRoute());
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Fetch events list when on home page
  useEffect(() => {
    if (slug) return;
    setLoadingEvents(true);
    const controller = new AbortController();
    api.events(controller.signal)
      .then((res) => {
        setEventsList(res.events);
        document.title = 'Fotos de eventos · Run Experiences';
        setLoadingEvents(false);
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setError(message(err));
          setLoadingEvents(false);
        }
      });
    return () => controller.abort();
  }, [slug]);

  // Fetch single event and general event photos when slug is set
  useEffect(() => {
    if (!slug) return;
    setLoading('event');
    setError('');
    const controller = new AbortController();

    Promise.all([
      api.event(slug, preview, controller.signal),
      api.allPhotos(slug, null, selectedDistance, preview, controller.signal)
    ])
      .then(([eventRes, photosRes]) => {
        setEvent(eventRes);
        document.title = `Fotos · ${eventRes.name}`;
        setAllEventPhotos(photosRes.photos);
        setNextCursor(photosRes.nextCursor);
        setLoading(null);
      })
      .catch((cause) => {
        if (!controller.signal.aborted) {
          setError(message(cause));
          setLoading(null);
        }
      });
    return () => controller.abort();
  }, [preview, slug]);

  // Trigger initial bib search if provided in URL
  useEffect(() => {
    if (!event || !initialBib || initialSearchStarted.current) return;
    initialSearchStarted.current = true;
    void submitSearch(initialBib, selectedDistance);
  }, [event, initialBib]);

  // Lightbox keyboard shortcut
  useEffect(() => {
    if (!selected) return;
    const close = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [selected]);

  useEffect(() => {
    if (!selected && !selfieOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [selected, selfieOpen]);

  async function loadRunner(value: Runner) {
    if (!slug) return;
    setRunner(value);
    setResults([]);
    setPhotos([]);
    setFacePhotos([]);
    setSelfieSearched(false);
    setLoading('photos');
    setError('');
    try {
      const response = await api.photos(slug, value.bib, null, preview);
      setRunner({ ...value, ...response.participant });
      setPhotos(response.photos);
      setNextCursor(response.nextCursor);
      const params = new URLSearchParams(window.location.search);
      params.set('bib', value.bib);
      if (preview) params.set('preview', preview);
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    } catch (cause) {
      setError(message(cause));
    } finally {
      setLoading(null);
    }
  }

  async function submitSearch(searchQuery?: string, distanceFilter?: string, eventObj?: React.FormEvent) {
    eventObj?.preventDefault();
    if (!slug) return;
    const value = (searchQuery !== undefined ? searchQuery : query).trim();
    const dist = distanceFilter !== undefined ? distanceFilter : selectedDistance;

    setLoading('search');
    setError('');
    setRunner(null);
    setPhotos([]);
    setFacePhotos([]);
    setSelfieSearched(false);
    setResults([]);

    // A previous exact-bib result must not remain in the address bar while a
    // different query, an empty state, or a name-selection state is shown.
    const params = new URLSearchParams(window.location.search);
    params.delete('bib');
    if (preview) params.set('preview', preview);
    const search = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`);

    try {
      if (!value && !dist) {
        // Reset to general all photos view
        const photosRes = await api.allPhotos(slug, null, '', preview);
        setAllEventPhotos(photosRes.photos);
        setNextCursor(photosRes.nextCursor);
        setLoading(null);
        return;
      }

      if (!value && dist) {
        // Filter general photos by distance
        const photosRes = await api.allPhotos(slug, null, dist, preview);
        setAllEventPhotos(photosRes.photos);
        setNextCursor(photosRes.nextCursor);
        setLoading(null);
        return;
      }

      const response = await api.search(slug, value, dist, preview);
      setAllEventPhotos([]);
      setNextCursor(null);
      setResults(response.results);
      if (response.results.length === 1) {
        await loadRunner(response.results[0]);
      }
    } catch (cause) {
      setError(message(cause));
      setLoading(null);
    } finally {
      setLoading((current) => (current === 'search' ? null : current));
    }
  }

  function handleDistanceChange(dist: string) {
    const newDist = selectedDistance === dist ? '' : dist;
    setSelectedDistance(newDist);
    void submitSearch(query, newDist);
  }

  async function loadMorePhotos() {
    if (!slug || !nextCursor) return;
    setLoading('more');
    try {
      if (runner) {
        const response = await api.photos(slug, runner.bib, nextCursor, preview);
        setPhotos((items) => [...items, ...response.photos]);
        setNextCursor(response.nextCursor);
      } else {
        const response = await api.allPhotos(slug, nextCursor, selectedDistance, preview);
        setAllEventPhotos((items) => [...items, ...response.photos]);
        setNextCursor(response.nextCursor);
      }
    } catch (cause) {
      setError(message(cause));
    } finally {
      setLoading(null);
    }
  }

  function resetSearch() {
    setQuery('');
    setSelectedDistance('');
    setRunner(null);
    setPhotos([]);
    setFacePhotos([]);
    setSelfieSearched(false);
    setResults([]);
    setNextCursor(null);
    setError('');
    if (slug) {
      window.history.replaceState({}, '', `${window.location.pathname}${preview ? `?preview=${encodeURIComponent(preview)}` : ''}`);
      void submitSearch('', '');
    }
    window.setTimeout(() => searchRef.current?.focus(), 0);
  }

  function openSelfieSearch() {
    setFacePhotos([]);
    setSelfieSearched(false);
    setSelfieSession((current) => current + 1);
    setSelfieOpen(true);
  }

  // --- HOME / EVENTS LIST VIEW ---
  if (!slug) {
    return (
      <div className="site-shell">
        <PublicHeader />

        <main className="recent-events-container photos-directory">
          <header className="recent-events-header">
            <h1>Fotos de eventos</h1>
          </header>

          {loadingEvents ? (
            <div className="loading-state">
              <LoaderCircle className="spin" size={38} />
              <span>Cargando galerías…</span>
            </div>
          ) : error ? (
            <div className="error-state" role="alert">
              <strong>Error al cargar los eventos</strong>
              <span>{error}</span>
            </div>
          ) : (
            <div className="events-grid">
              {!eventsList.some(item => item.slug === 'reto-tenan-2026') && <article className="directory-card pending-event">
                <div className="directory-cover pending-cover"><img src="/reto-tenan-2026.png" alt="Reto Tenan" /><span>2026</span></div>
                <div className="directory-card-body">
                  <h2>Reto Tenan</h2>
                  <p>Fotografías en preparación</p>
                  <p className="directory-count">Estamos revisando las fotos y sus dorsales.</p>
                  <button className="directory-cta" disabled>Próximamente</button>
                </div>
              </article>}
              {eventsList.map((item) => {
                const details = eventDetails[item.slug];
                return (
                  <article key={item.slug} className="directory-card">
                    <a className={`directory-cover${item.slug === 'reto-tenan-2026' ? ' pending-cover' : ''}`} href={`/eventos/${encodeURIComponent(item.slug)}`} tabIndex={-1} aria-hidden="true">
                      <img src={item.slug === 'reto-tenan-2026' ? '/reto-tenan-2026.png' : details?.cover || item.coverThumbs[0]} alt="" loading="lazy" />
                      {/20\d{2}/.test(item.name) && <span>{item.name.match(/20\d{2}/)?.[0]}</span>}
                    </a>
                    <div className="directory-card-body">
                      <h2>{item.name.replace(/\s+20\d{2}$/, '')}</h2>
                      {details && <><p><Calendar size={15} />{details.date}</p><p><MapPin size={15} />{details.location}</p></>}
                      <p className="directory-count"><ImageIcon size={15} />{item.counts.publishedPhotos.toLocaleString('es-MX')} fotografías disponibles</p>
                      <a className="directory-cta" href={`/eventos/${encodeURIComponent(item.slug)}`} aria-label={`Ver fotos de ${item.name}`}>Ver fotografías<ChevronRight size={17} /></a>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </main>

        <footer className="site-footer">
          <div className="footer-content">
            <p><a href="https://runexperiences.com.mx/">Run Experiences</a> · Fotografías de nuestras carreras</p>
            <p className="footer-credits">Powered by Orbyte</p>
          </div>
        </footer>
      </div>
    );
  }

  // --- SINGLE EVENT GALLERY VIEW ---
  if (loading === 'event') {
    return (
      <main className="loading-page">
        <LoaderCircle className="spin" size={34} />
        <span>Abriendo fotografías del evento…</span>
      </main>
    );
  }

  const activePhotos = runner ? photos : allEventPhotos;

  return (
    <div className="site-shell">
      {preview ? <div className="preview-banner">Vista privada · esta versión todavía no es pública</div> : null}

      <PublicHeader />

      <main className="content">
        <a className="nav-btn outline" href="/"><ArrowLeft size={16} /> Ver todas las carreras</a>
        <section className="search-section" aria-labelledby="page-title">
          <div className="event-header-info">
            <h1 id="page-title">{event?.name || 'Fotos del evento'}</h1>
            {event?.counts?.publishedPhotos ? (
              <span className="photo-count-badge">
                <ImageIcon size={14} /> {event.counts.publishedPhotos.toLocaleString()} fotografías confirmadas
              </span>
            ) : null}
          </div>

          {/* DISTANCE FILTER PILLS */}
          {event?.distances && event.distances.length > 0 ? (
            <div className="distance-filter-container">
              <span className="distance-label">
                <Filter size={15} /> Filtrar por distancia:
              </span>
              <div className="distance-pills">
                <button
                  type="button"
                  className={`distance-pill ${!selectedDistance ? 'active' : ''}`}
                  onClick={() => handleDistanceChange('')}
                >
                  Todas
                </button>
                {event.distances.map((dist) => (
                  <button
                    key={dist}
                    type="button"
                    className={`distance-pill ${selectedDistance === dist ? 'active' : ''}`}
                    onClick={() => handleDistanceChange(dist)}
                  >
                    {dist}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <form onSubmit={(e) => submitSearch(undefined, undefined, e)}>
            <label className="search-input">
              <span className="sr-only">Número de corredor o nombre</span>
              <Search size={20} />
              <input
                ref={searchRef}
                autoComplete="off"
                value={query}
                onChange={(item) => setQuery(item.target.value)}
                placeholder="Número exacto (ej. 12) o nombre"
                aria-label="Número de corredor o nombre"
                aria-describedby="search-help"
              />
              {query ? (
                <button type="button" onClick={() => setQuery('')} aria-label="Limpiar búsqueda">
                  <X size={17} />
                </button>
              ) : null}
            </label>

            <button className="search-button" disabled={loading === 'search'}>
              {loading === 'search' ? <LoaderCircle className="spin" size={20} /> : null} Buscar
            </button>
          </form>

          {event?.faceSearch ? (
            <>
              <div className="search-divider">
                <span>o</span>
              </div>
              <button className="selfie-search-button" onClick={openSelfieSearch}>
                <Camera size={20} /> Buscar con una selfie
              </button>
            </>
          ) : null}

          <p id="search-help">Escribe tu número de dorsal exacto (ej. <strong>12</strong>) para ver únicamente tus resultados sin coincidencias parciales.</p>
        </section>

        <aside className="photo-disclaimer">
          <ImageIcon size={22} />
          <div>
            <strong>Tu foto puede estar en la galería aunque no aparezca en la búsqueda.</strong>
            <p>La luz, el movimiento, un número tapado o colocado en la espalda, o un rostro parcialmente cubierto pueden impedir que reconozcamos correctamente el dorsal o la cara. Te invitamos a recorrer las fotografías para encontrarla.</p>
          </div>
        </aside>

        {error ? (
          <div className="error-state" role="alert">
            <strong>No pudimos mostrar las fotos</strong>
            <span>{error}</span>
          </div>
        ) : null}

        {results.length > 1 ? (
          <section className="runner-results">
            <h2>Selecciona a tu corredor</h2>
            {results.map((item) => (
              <button key={item.bib} onClick={() => loadRunner(item)}>
                <UserRound size={22} />
                <span>
                  <strong>{item.name}</strong>
                  <small>Dorsal #{item.bib} · {item.distance}</small>
                </span>
                <em>{item.photoCount} foto{item.photoCount === 1 ? '' : 's'}</em>
              </button>
            ))}
          </section>
        ) : null}

        {runner ? (
          <section className="runner-section">
            <header>
              <div className="runner-icon">
                <UserRound size={31} />
              </div>
              <div>
                <h2>Dorsal #{runner.bib} <span className="exact-match-tag">Coincidencia exacta</span></h2>
                <p>{runner.name}</p>
                <strong>{runner.distance}</strong>
                {slug === 'reto-tenan-2026' ? <p><a href={`https://runexperiences.com.mx/resultados-reto-tenan/?dorsal=${encodeURIComponent(runner.bib)}`}>Ver mi resultado y mis tiempos →</a></p> : null}
              </div>
            </header>
          </section>
        ) : null}

        {/* GALLERY PHOTO GRID */}
        <section className="gallery-section">
          {!runner && !facePhotos.length && allEventPhotos.length > 0 ? (
            <div className="gallery-header">
              <h3>
                {selectedDistance
                  ? `Galería de fotografías (${selectedDistance})`
                  : `Galería completa de fotos (${event?.counts?.publishedPhotos || 0})`}
              </h3>
            </div>
          ) : null}

          {loading === 'photos' || loading === 'search' ? (
            <div className="photo-loading">
              <LoaderCircle className="spin" />
              <span>Cargando fotografías…</span>
            </div>
          ) : facePhotos.length > 0 ? null /* selfie results shown below */ : activePhotos.length ? (
            <>
              <div className="photo-grid">
                {activePhotos.map((photo, index) => (
                  <article key={photo.id}>
                    <button className="photo-open" onClick={() => setSelected(photo)} aria-label={`Abrir foto ${index + 1}`}>
                      <img
                        src={photo.thumbUrl}
                        alt={`${runner?.name || event?.name || 'Fotografía de carrera'}`}
                        width={photo.width}
                        height={photo.height}
                        loading={index < 4 ? 'eager' : 'lazy'}
                        decoding="async"
                      />
                      <span>
                        <ZoomIn size={17} /> Ver
                      </span>
                    </button>
                    {runner && photo.matchSource && <small className="match-source">{photo.matchSource === 'face' ? 'Coincidencia facial revisada' : 'Dorsal visible'}</small>}
                    <a href={photo.downloadUrl} download={`${slug}-${runner?.bib || 'foto'}-${index + 1}.jpg`} aria-label={`Descargar foto ${index + 1}`}>
                      <Download size={17} /> <span>Descargar</span>
                    </a>
                  </article>
                ))}
              </div>

              {nextCursor ? (
                <button className="more-button" onClick={loadMorePhotos} disabled={loading === 'more'}>
                  {loading === 'more' ? <LoaderCircle className="spin" size={18} /> : null} Ver más fotos
                </button>
              ) : null}
            </>
          ) : !loading && results.length === 0 && (query || selectedDistance) ? (
            <section className="empty-state">
              <UserRound size={36} />
              <h2>No encontramos coincidencias</h2>
              <p>
                Revisa el número de dorsal o intenta con al menos tres letras del nombre.
                {selectedDistance ? ` (Filtro activo: ${selectedDistance})` : ''}
              </p>
            </section>
          ) : null}
        </section>

        {selfieSearched && facePhotos.length === 0 ? (
          <section className="empty-state">
            <Camera size={36} />
            <h2>No encontramos coincidencias</h2>
            <p>La búsqueda por selfie no encontró fotos que coincidan con tu rostro. Intenta otra vez con buena iluminación y el rostro bien centrado.</p>
            <button className="new-search" style={{ marginTop: 8 }} onClick={openSelfieSearch}>
              <Camera size={18} /> Intentar de nuevo
            </button>
          </section>
        ) : null}

        {facePhotos.length > 0 ? (
          <section className="runner-section face-results">
            <header>
              <div className="runner-icon">
                <Camera size={29} />
              </div>
              <div>
                <h2>Resultados de tu selfie</h2>
                <p>Ordenadas de mayor a menor parecido. Las posibles coincidencias pueden incluir a otras personas.</p>
                <strong>{facePhotos.length} foto{facePhotos.length === 1 ? '' : 's'}</strong>
              </div>
            </header>
            <div className="photo-grid">
              {facePhotos.map((photo, index) => (
                <article key={photo.id}>
                  <button className="photo-open" onClick={() => setSelected(photo)} aria-label={`Abrir coincidencia ${index + 1}`}>
                    <img
                      src={photo.thumbUrl}
                      alt={`Posible coincidencia ${index + 1} en ${event?.name || 'el evento'}`}
                      width={photo.width}
                      height={photo.height}
                      loading={index < 2 ? 'eager' : 'lazy'}
                      decoding="async"
                    />
                    <span>
                      <ZoomIn size={17} /> Ver
                    </span>
                  </button>
                  <small className="match-source">{(photo.similarity || 0) >= 0.72 ? 'Mayor coincidencia facial' : 'Posible coincidencia'}</small>
                  <a href={photo.downloadUrl} download={`${slug}-selfie-${index + 1}.jpg`} aria-label={`Descargar coincidencia ${index + 1}`}>
                    <Download size={17} /> <span>Descargar</span>
                  </a>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {runner || facePhotos.length || query || selectedDistance ? (
          <button className="new-search" onClick={resetSearch}>
            <RefreshCw size={20} /> Restablecer filtros / Ver todas las fotos
          </button>
        ) : null}
      </main>

      <footer className="site-footer">
        <div className="footer-content">
          <p>Run Experiences · Fotografías del evento</p>
          <p className="footer-credits">Powered by Orbyte</p>
        </div>
      </footer>

      {selected ? (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label="Fotografía ampliada" onClick={() => setSelected(null)}>
          <button type="button" onClick={() => setSelected(null)} aria-label="Cerrar">
            <X size={23} />
          </button>
          <div className="lightbox-content" onClick={(item) => item.stopPropagation()}>
            <img src={selected.displayUrl} alt={`${runner?.name || 'Corredor'} en carrera`} />
            <a href={selected.downloadUrl} download>
              <Download size={19} /> Descargar foto
            </a>
          </div>
        </div>
      ) : null}

      {selfieOpen ? (
        <Suspense
          fallback={
            <div className="selfie-overlay">
              <div className="selfie-loading">
                <LoaderCircle className="spin" />
                <span>Preparando cámara…</span>
              </div>
            </div>
          }
        >
          <SelfieSearch
            key={selfieSession}
            slug={slug}
            preview={preview}
            onClose={() => setSelfieOpen(false)}
            onResults={(items) => {
              setRunner(null);
              setResults([]);
              setPhotos([]);
              setFacePhotos(items);
              setSelfieSearched(true);
              setSelfieOpen(false);
            }}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
